import type { Direction, RiskInputs, RiskResult, SymbolRules } from "../types";

export const LEVERAGE_OPTIONS = [1, 2, 3, 5, 10, 20, 25, 50, 75, 100, 125];

export function precisionFromStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  const text = step.toFixed(12).replace(/0+$/, "");
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}

export function floorToStep(value: number, step: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(step) || step <= 0) return 0;
  const precision = precisionFromStep(step);
  const stepped = Math.floor((value + step * 1e-9) / step) * step;
  return Number(stepped.toFixed(precision));
}

export function roundToTick(value: number, tick: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(tick) || tick <= 0) return value;
  const precision = precisionFromStep(tick);
  return Number((Math.round(value / tick) * tick).toFixed(precision));
}

export function stopPriceFromPercent(
  entryPrice: number,
  stopPercent: number,
  direction: Direction,
): number {
  const multiplier = direction === "long" ? 1 - stopPercent / 100 : 1 + stopPercent / 100;
  return entryPrice * multiplier;
}

export function isStopOnValidSide(
  direction: Direction,
  entryPrice: number,
  stopPrice: number,
): boolean {
  return direction === "long" ? stopPrice < entryPrice : stopPrice > entryPrice;
}

function chooseLeverage(notional: number, maxMargin: number): number {
  if (!(maxMargin > 0)) return 1;
  const candidate = LEVERAGE_OPTIONS.find((leverage) => notional / leverage <= maxMargin);
  return candidate ?? LEVERAGE_OPTIONS.at(-1)!;
}

function approximateLiquidation(
  direction: Direction,
  entryPrice: number,
  leverage: number,
): number | null {
  if (leverage <= 1) return null;
  // 这里只给无账户条件下的粗略提醒线。真实强平价还取决于维持保证金档位、手续费和账户余额。
  const maintenanceBuffer = 0.005;
  const ratio = direction === "long"
    ? 1 - 1 / leverage + maintenanceBuffer
    : 1 + 1 / leverage - maintenanceBuffer;
  return Math.max(0, entryPrice * ratio);
}

export function calculateRisk(inputs: RiskInputs, rules: SymbolRules): RiskResult {
  const { direction, entryPrice, stopPrice, riskBudget, rewardRiskRatio, maxMargin } = inputs;
  if (![entryPrice, stopPrice, riskBudget, rewardRiskRatio].every((value) => Number.isFinite(value) && value > 0)) {
    throw new RangeError("开仓价、止损价、最大亏损和盈亏比都必须大于 0");
  }
  if (!isStopOnValidSide(direction, entryPrice, stopPrice)) {
    throw new RangeError(direction === "long" ? "开多的止损价必须低于开仓价" : "开空的止损价必须高于开仓价");
  }

  const priceDistance = Math.abs(entryPrice - stopPrice);
  const stopPercent = (priceDistance / entryPrice) * 100;
  const rawQuantity = riskBudget / priceDistance;
  let quantity = floorToStep(rawQuantity, rules.quantityStep);
  const warnings: string[] = [];

  if (quantity < rules.minQuantity) {
    warnings.push(`风险预算不足以满足最小数量 ${rules.minQuantity} ${rules.baseAsset}，请放宽止损或提高预算`);
    quantity = 0;
  }
  if (rules.maxQuantity > 0 && quantity > rules.maxQuantity) {
    quantity = floorToStep(rules.maxQuantity, rules.quantityStep);
    warnings.push(`数量已按市场单上限 ${rules.maxQuantity} ${rules.baseAsset} 截断`);
  }

  const notional = quantity * entryPrice;
  const actualRisk = quantity * priceDistance;
  const takeProfitLinked = inputs.takeProfitLinked !== false;
  const expectedReward = takeProfitLinked ? actualRisk * rewardRiskRatio : 0;
  const takeProfitPrice = !takeProfitLinked
    ? entryPrice
    : direction === "long"
      ? entryPrice + priceDistance * rewardRiskRatio
      : entryPrice - priceDistance * rewardRiskRatio;
  const suggestedLeverage = chooseLeverage(notional, maxMargin);
  const estimatedMargin = suggestedLeverage > 0 ? notional / suggestedLeverage : notional;

  if (quantity > 0 && rules.minNotional > 0 && notional < rules.minNotional) {
    warnings.push(`名义价值 ${notional.toFixed(2)} USDT 低于交易所最低要求 ${rules.minNotional} USDT`);
  }
  if (maxMargin > 0 && estimatedMargin > maxMargin) {
    warnings.push(`即使按 ${suggestedLeverage}x 估算，保证金仍高于 ${maxMargin} USDT 上限`);
  }
  if (stopPercent < 0.25) {
    warnings.push("止损距离小于 0.25%，数量会被明显放大，手续费与滑点可能让实际亏损超出预算");
  }
  if (stopPercent > 25) {
    warnings.push("止损距离超过 25%，请再次核对方向和止损位置");
  }
  if (suggestedLeverage >= 50) {
    warnings.push("建议杠杆较高；公开行情无法确认你的账户可用杠杆与实际强平价");
  }
  if (inputs.marginMode === "cross") {
    warnings.push("全仓强平价依赖整个合约账户余额，本工具无法准确估算");
  }

  return {
    quantity,
    rawQuantity,
    notional,
    actualRisk,
    priceDistance,
    stopPercent,
    takeProfitPrice,
    expectedReward,
    suggestedLeverage,
    estimatedMargin,
    approximateLiquidationPrice: inputs.marginMode === "isolated"
      ? approximateLiquidation(direction, entryPrice, suggestedLeverage)
      : null,
    quantityPrecision: precisionFromStep(rules.quantityStep),
    pricePrecision: precisionFromStep(rules.priceTick),
    warnings,
  };
}

export function formatByPrecision(value: number, precision: number): string {
  if (!Number.isFinite(value)) return "—";
  return value.toLocaleString("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false,
  });
}
